import { HttpClient } from '@angular/common/http';
import { Injectable, inject, signal } from '@angular/core';
import { forkJoin, map, Observable, of, tap } from 'rxjs';

import { API_V1_URL } from '../../../core/config/api.config';
import { AuthService } from '../../../core/services/auth.service';
import { Inventory } from '../models/inventory.model';
import { InventoryArticle } from '../models/inventory-article.model';
import { InventoryDetail } from '../models/inventory-detail.model';
import { InventoryStatus } from '../models/inventory-status.enum';
import { InventurEinheit } from '../models/inventur-einheit';
import { InventurKatalogArtikel } from '../models/inventur-katalog-artikel.model';
import { InventoryUnitOption } from '../models/inventory-unit-option.model';
import { ArtikelEinheitDto, normalizeArtikelEinheiten, waehleArtikelEinheit } from '../../../shared/models/artikel-einheit.model';

interface BackendInventoryOverviewDto {
  inventurPlanId: number;
  inventurListeId: number | null;
  filialeId: number;
  filialNummer: number;
  filialName: string;
  bezeichnung: string;
  intervallTyp: string;
  intervallStart: string | null;
  intervallEnde: string | null;
  anzahlTageBearbeiten: number;
  datum: string | null;
  datumStart: string;
  datumEnde: string;
  status: 'Offen' | 'In Bearbeitung' | 'InBearbeitung' | 'Erfasst' | 'Abgeschlossen';
  artikelGesamt: number;
  artikelErfasst: number;
  wert: number;
  istKritisch: boolean;
}

type BackendInventoryUnitDto = ArtikelEinheitDto;

interface BackendInventoryPositionDto {
  id: string | null;
  artikelnummer: number;
  artikelName: string;
  inventurMenge: number;
  inventurEinheitId: number | null;
  verkaufspreis: number;
  wert: number;
  istErfasst: boolean;
  einheiten: readonly BackendInventoryUnitDto[];
}

interface BackendInventoryDetailDto {
  inventurPlanId: number;
  inventurListeId: number | null;
  filialeId: number;
  filialNummer: number;
  filialName: string;
  bezeichnung: string;
  intervallTyp: string;
  intervallStart: string | null;
  intervallEnde: string | null;
  anzahlTageBearbeiten: number;
  datumStart: string;
  datumEnde: string;
  status: 'Offen' | 'In Bearbeitung' | 'InBearbeitung' | 'Erfasst' | 'Abgeschlossen';
  positionen: BackendInventoryPositionDto[];
  istKritisch: boolean;
}

interface BackendAvailableInventoryArticleDto {
  artikelnummer: number;
  artikelName: string;
  einheiten: readonly BackendInventoryUnitDto[];
}

export interface SaveInventoryPositionRequest {
  id?: string | null;
  artikelnummer: number;
  inventurMenge: number;
  inventurEinheitId: number;
}

export interface SavedInventoryPosition {
  id: string | null;
  artikelnummer: number;
  inventurMenge: number;
  inventurEinheitId: number;
}

export interface InventoryLockSession {
  lockId: string;
  expiresAt: string;
}

@Injectable({ providedIn: 'root' })
export class InventoryService {
  private readonly http = inject(HttpClient);
  private readonly authService = inject(AuthService);
  private readonly apiBaseUrl = API_V1_URL;
  private readonly activeLocks = new Map<string, InventoryLockSession>();
  private readonly anzahlOffenState = signal<number | undefined>(undefined);
  private readonly availableArticlesState = signal<readonly InventurKatalogArtikel[]>([]);
  private readonly unitNamesById = new Map<number, string>();

  readonly anzahlOffen = this.anzahlOffenState.asReadonly();

  getInventories(): Observable<Inventory[]> {
    return this.http
      .get<BackendInventoryOverviewDto[]>(`${this.apiBaseUrl}/inventuren`)
      .pipe(map((items) => items.map((item) => this.mapInventory(item))));
  }

  getAnzahlOffen(): Observable<number> {
    return this.http
      .get<number>(`${this.apiBaseUrl}/inventuren/anzahl-offen`)
      .pipe(tap((anzahl) => this.anzahlOffenState.set(anzahl)));
  }

  getInventoryDetail(id: string, filialNummer: number): Observable<InventoryDetail> {
    return this.http
      .get<BackendInventoryDetailDto>(
        `${this.apiBaseUrl}/inventuren/${Number(id)}/filialen/${filialNummer}`
      )
      .pipe(map((item) => this.mapInventoryDetail(item)));
  }

  loadAvailableArticles(
    id: string,
    filialNummer: number
  ): Observable<InventurKatalogArtikel[]> {
    return this.http
      .get<BackendAvailableInventoryArticleDto[]>(
        `${this.apiBaseUrl}/inventuren/${Number(id)}/filialen/${filialNummer}/verfuegbare-artikel`
      )
      .pipe(
        map((items) =>
          items
            .map((item) => {
              const options = this.mapUnitOptions(item.einheiten, null, 0);
              const option = waehleArtikelEinheit(options);

              if (!option) return null;

              return {
                artikelnummer: String(item.artikelnummer),
                artikelname: item.artikelName,
                warengruppe: '',
                verkaufspreis: 0,
                inventurEinheit: option.inventurEinheit,
                inventurEinheitId: option.id ?? this.unitIdFromLabel(option.inventurEinheit),
                grundeinheit:
                  this.isKilogrammUnit(option.inventurEinheit) ? 'Kilogramm' : 'Stück',
                verfuegbareEinheiten: options,
              } satisfies InventurKatalogArtikel;
            })
            .filter((item): item is InventurKatalogArtikel => item !== null)
        ),
        tap((items) => this.availableArticlesState.set(items))
      );
  }

  acquireInventoryLock(
    id: string,
    filialNummer: number
  ): Observable<InventoryLockSession> {
    const key = this.inventoryLockKey(id, filialNummer);

    return this.http
      .post<InventoryLockSession>(`${this.apiBaseUrl}/locks/acquire`, {
        keys: [key],
      })
      .pipe(tap((session) => this.activeLocks.set(key, session)));
  }

  heartbeatInventoryLock(
    id: string,
    filialNummer: number
  ): Observable<InventoryLockSession | null> {
    const key = this.inventoryLockKey(id, filialNummer);
    const session = this.activeLocks.get(key);

    if (!session) return of(null);

    return this.http
      .put<InventoryLockSession>(
        `${this.apiBaseUrl}/locks/${session.lockId}/heartbeat`,
        null
      )
      .pipe(tap((refreshed) => this.activeLocks.set(key, refreshed)));
  }

  getInventoryLockSession(
    id: string,
    filialNummer: number
  ): InventoryLockSession | null {
    return this.activeLocks.get(this.inventoryLockKey(id, filialNummer)) ?? null;
  }

  releaseInventoryLock(id: string, filialNummer: number): Observable<void> {
    const key = this.inventoryLockKey(id, filialNummer);
    const session = this.activeLocks.get(key);

    if (!session) return of(void 0);

    // The backend logout command already releases all locks for this session.
    // After local auth has been cleared, only forget the cached lock locally.
    if (!this.authService.getToken()) {
      this.activeLocks.delete(key);
      return of(void 0);
    }

    return this.http
      .delete<void>(`${this.apiBaseUrl}/locks/${session.lockId}`)
      .pipe(tap(() => this.activeLocks.delete(key)));
  }

  releaseInventoryLockBeforeUnload(id: string, filialNummer: number): void {
    const key = this.inventoryLockKey(id, filialNummer);
    const session = this.activeLocks.get(key);

    if (!session) return;

    const token = this.authService.getToken();
    this.activeLocks.delete(key);

    if (!token) return;

    const headers: Record<string, string> = {
      Authorization: `Bearer ${token}`,
    };

    void fetch(`${this.apiBaseUrl}/locks/${session.lockId}`, {
      method: 'DELETE',
      keepalive: true,
      headers,
    });
  }

  clearInventoryLock(id: string, filialNummer: number): void {
    this.activeLocks.delete(this.inventoryLockKey(id, filialNummer));
  }

  savePositions(
    id: string,
    filialNummer: number,
    positions: readonly SaveInventoryPositionRequest[]
  ): Observable<SavedInventoryPosition[]> {
    const toCreate = positions.filter((position) => !position.id);
    const toUpdate = positions.filter((position) => Boolean(position.id));
    const requests: Observable<SavedInventoryPosition[]>[] = [];
    const url = `${this.apiBaseUrl}/inventuren/${Number(id)}/filialen/${filialNummer}`;

    if (toCreate.length > 0) {
      requests.push(this.http.post<SavedInventoryPosition[]>(url, toCreate));
    }

    if (toUpdate.length > 0) {
      requests.push(this.http.put<SavedInventoryPosition[]>(url, toUpdate));
    }

    if (requests.length === 0) return of([]);
    if (requests.length === 1) return requests[0]!;

    return forkJoin(requests).pipe(map((groups) => groups.flat()));
  }

  closeInventory(
    id: string,
    filialNummer: number,
    bestaetigt = false
  ): Observable<void> {
    return this.http.post<void>(
      `${this.apiBaseUrl}/inventuren/${Number(id)}/filialen/${filialNummer}/abschluss`,
      {},
      { params: { bestaetigt: String(bestaetigt) } }
    );
  }

  searchKatalog(query: string, limit = 30): InventurKatalogArtikel[] {
    const term = query.trim().toLowerCase();
    const all = this.availableArticlesState();

    if (!term) return all.slice(0, limit);

    return all
      .filter(
        (item) =>
          item.artikelname.toLowerCase().includes(term) ||
          item.artikelnummer.includes(term)
      )
      .slice(0, limit);
  }

  istPlausibilitaetsWarnung(): boolean {
    return false;
  }

  unitIdFromLabel(label: InventurEinheit): number {
    const normalized = label.trim().toLocaleLowerCase('de-DE');
    const cached = [...this.unitNamesById.entries()].find(
      ([, value]) => value.trim().toLocaleLowerCase('de-DE') === normalized
    );
    if (cached) return cached[0];

    return 0;
  }

  private inventoryLockKey(id: string, filialNummer: number): string {
    return `inventur:${Number(id)}:${filialNummer}`;
  }

  private mapInventory(item: BackendInventoryOverviewDto): Inventory {
    return {
      id: String(item.inventurPlanId),
      filialNummer: item.filialNummer,
      filialeName: item.filialName ?? '',
      bezeichnung: item.bezeichnung,
      datum: item.datum ?? item.datumStart,
      status: this.mapStatus(item.status),
      anzahlArtikel: item.artikelGesamt,
      erfassteArtikel: item.artikelErfasst,
      inventurWert: item.wert,
      differenzWert: 0,
      hoheAbweichungen: 0,
      restzeitMinuten: Number.MAX_SAFE_INTEGER,
      istKritisch: item.istKritisch,
    };
  }

  private mapInventoryDetail(item: BackendInventoryDetailDto): InventoryDetail {
    return {
      id: String(item.inventurPlanId),
      filialNummer: item.filialNummer,
      filialeName: item.filialName ?? '',
      bezeichnung: item.bezeichnung,
      status: this.mapStatus(item.status),
      istKritisch: item.istKritisch,
      artikel: item.positionen.map((position, index) => this.mapArticle(position, index)),
    };
  }

  private mapArticle(item: BackendInventoryPositionDto, index: number): InventoryArticle {
    const options = this.mapUnitOptions(
      item.einheiten,
      item.inventurEinheitId,
      item.verkaufspreis
    );
    const option =
      waehleArtikelEinheit(options, item.inventurEinheitId) ??
      this.defaultUnitOption(item.verkaufspreis);

    return {
      id: item.id ?? `${item.artikelnummer}#${option.id ?? 0}#${index}`,
      positionId: item.id ?? null,
      warengruppe: '',
      artikelnummer: String(item.artikelnummer),
      artikelname: item.artikelName,
      verkaufspreis: item.verkaufspreis,
      sollBestand: 0,
      istBestand: item.istErfasst ? item.inventurMenge : null,
      inventurEinheit: option.inventurEinheit,
      inventurEinheitId: option.id ?? this.unitIdFromLabel(option.inventurEinheit),
      umrechnungsfaktor: option.umrechnungsfaktor,
      grundeinheit: this.isKilogrammUnit(option.inventurEinheit) ? 'Kilogramm' : 'Stück',
      verfuegbareEinheiten: options,
      letzteAenderungZeit: null,
      letzteAenderungBenutzer: null,
    };
  }

  private mapUnitOptions(
    items: readonly BackendInventoryUnitDto[] | undefined,
    currentUnitId: number | null,
    verkaufspreis: number
  ): readonly InventoryUnitOption[] {
    const mapped: InventoryUnitOption[] = normalizeArtikelEinheiten(items).map((item) => {
      this.unitNamesById.set(item.id, item.name);
      return {
        id: item.id,
        inventurEinheit: item.name,
        verkaufspreis,
        // The table's existing Faktor column shows 1 / umrechnungsfaktor.
        // Thus 120 Stück per Karton => 1/120 (display: 120).
        umrechnungsfaktor: 1 / item.faktorZurKleinstenEinheit,
        menge: item.menge,
        baseEinheitId: item.baseEinheitId,
        nr: item.nr,
        isDefault: item.isDefault,
      };
    });

    if (
      currentUnitId !== null &&
      currentUnitId >= 0 &&
      !mapped.some((item) => item.id === currentUnitId)
    ) {
      return [...mapped, this.unitOption(currentUnitId, verkaufspreis)];
    }

    return mapped.length > 0 ? mapped : [this.defaultUnitOption(verkaufspreis)];
  }

  private defaultUnitOption(verkaufspreis: number): InventoryUnitOption {
    return {
      id: 0,
      inventurEinheit: this.mapUnitName('', 0),
      verkaufspreis,
      umrechnungsfaktor: 1,
    };
  }

  private unitOption(id: number, verkaufspreis: number): InventoryUnitOption {
    return {
      id,
      inventurEinheit: this.mapUnitName('', id),
      verkaufspreis,
      umrechnungsfaktor: 1,
    };
  }

  private mapUnitName(name: string, id: number): InventurEinheit {
    const backendName = name?.trim();
    if (backendName) return backendName;

    const cached = this.unitNamesById.get(id);
    if (cached) return cached;

    return `Einheit ${id}`;
  }

  private isKilogrammUnit(name: string): boolean {
    const normalized = name.trim().toLocaleLowerCase('de-DE');
    return (
      normalized === 'kg' ||
      normalized.includes('kilogramm') ||
      normalized.includes('kilo')
    );
  }

  private mapStatus(
    status: BackendInventoryOverviewDto['status'] | BackendInventoryDetailDto['status']
  ): InventoryStatus {
    switch (status) {
      case 'Offen':
        return InventoryStatus.Offen;
      case 'In Bearbeitung':
      case 'InBearbeitung':
        return InventoryStatus.InBearbeitung;
      case 'Erfasst':
        return InventoryStatus.Erfasst;
      case 'Abgeschlossen':
        return InventoryStatus.Abgeschlossen;
    }
  }
}
